import { Component, OnInit } from '@angular/core';
import { trigger, transition, style, animate } from '@angular/animations';
import { TranslatePipe } from '@ngx-translate/core'; // tslint:disable-line
import { DialogService } from './dialog.service';

@Component({
    selector: 'modal-confirm',
    templateUrl: './confirm-dialog.component.html',
    styleUrls: ['../../assets/theme/css/ConfirmDialog.css'],
    animations: [
        trigger('opening', [
            transition(':enter', [
                style({'opacity': 0}),
                animate('300ms', style({opacity: 1}))
            ])
        ])
    ]

})
export class ConfirmDialogComponent implements OnInit {

    public isActive: boolean = false;

    public dialogType: string;
    public titleLabel: string;
    public messageLabel: string;
    public details: string;
    public okLabel: string;
    public cancelLabel: string;

    private _deferred;

    private _defaults = {
        dialogType: 'confirm',
        titleLabel: '',
        messageLabel: 'are-you-sure',
        details: undefined,
        cancelLabel: 'cancel',
        okLabel: 'ok'
    };

    constructor(confirmService: DialogService) {
        confirmService.alert = this.alert.bind(this);
        confirmService.confirm = this.confirm.bind(this);
    }

    // ---
    // PUBLIC METHODS.
    // ---

    public alert(message = this._defaults.messageLabel, title = this._defaults.titleLabel) {
        return this._activate('alert', message, title);
    }

    public confirm(message = this._defaults.messageLabel, title = this._defaults.titleLabel, details = this._defaults.details) {
        return this._activate('confirm', message, title, details);
    }

    public isConfirm() {
        return this.dialogType === 'confirm';
    }

    public onConfirm() {
        this._hideDialog();
        this._deferred.resolve();
    }

    public onCancel() {
        this._hideDialog();
        this._deferred.reject();
    }

    // ---
    // PRIVATE METHODS.
    // ---

    private _activate(type = this._defaults.dialogType, message = this._defaults.messageLabel, title = this._defaults.titleLabel, details = this._defaults.details) {
        this._setDialog(type, message, title, details);

        this._showDialog();

        this._deferred = new Deferred<void>();
        return this._deferred.promise;
    }

    private _setDialog(type = this._defaults.dialogType, message = this._defaults.messageLabel, title = this._defaults.titleLabel, details = this._defaults.details) {
        this.dialogType = type;
        this.titleLabel = title;
        this.messageLabel = message;
        this.details = details;
        this.okLabel = this._defaults.okLabel;
        this.cancelLabel = this._defaults.cancelLabel;
    }

    private _showDialog() {
        this.isActive = true;
    }

    private _hideDialog() {
        this.isActive = false;
    }

    ngOnInit(): any {
    }
}

/**
 * A lightweight Deferred implementation, using native Promises.
 *
 * ### Example
 *
 * ```typescript
 * var deferred = new Deferred<string>();
 *
 * // set up handlers
 * deferred.promise.then(function (value) {}, function (error) {});
 *
 * // resolve
 * deferred.resolve('a value');
 *
 * // reject
 * deferred.reject(new Error('oh no!'));
 *
 * ```
 *
 */
class Deferred<T> {

    // The Deferred’s Promise object.
    promise: Promise<T>;

    // Resolve the Deferred object and call any 'done' callbacks with the given args.
    resolve: (value?: T | PromiseLike<T>) => void;

    // Reject a Deferred object and call any 'fail' callbacks with the given args.
    reject:  (reason?: any) => void;

    constructor() {
        this.promise = new Promise<T>((resolve, reject) => {
            this.resolve = resolve;
            this.reject  = reject;
        });
    }
}
